import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Info } from 'lucide-react';
import { Button } from '@/components/ui/button';

const MarketplaceSettingsSection = ({ settings, setSettings }) => {
  const updateSetting = (key, value) => {
    setSettings(prev => ({ ...prev, [key]: value }));
  };

  return (
    <div className="space-y-8" data-testid="marketplace-settings-section">
      {/* Public Strategy and Subscribers */}
      <div className="grid md:grid-cols-2 gap-8">
        <div>
          <div className="flex items-center gap-2 mb-4">
            <h4 className="text-base font-semibold text-gray-900">Public Strategy</h4>
            <Button variant="ghost" size="sm" className="h-auto p-0">
              <Info className="w-4 h-4 text-gray-400" />
            </Button>
          </div>
          <Select
            value={settings.public_strategy || 'No'}
            onValueChange={(v) => updateSetting('public_strategy', v)}
            className="w-full"
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="No">No</SelectItem>
              <SelectItem value="Yes">Yes</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div>
          <div className="flex items-center gap-2 mb-4">
            <h4 className="text-base font-semibold text-gray-900">Subscribers with Private Link Only</h4>
            <Button variant="ghost" size="sm" className="h-auto p-0">
              <Info className="w-4 h-4 text-gray-400" />
            </Button>
          </div>
          <Select
            value={settings.subscribers_private_link_only || 'Yes'}
            onValueChange={(v) => updateSetting('subscribers_private_link_only', v)}
            className="w-full"
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="Yes">Yes</SelectItem>
              <SelectItem value="No">No</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Fee Structure */}
      <div className="grid md:grid-cols-5 gap-4">
        <div>
          <div className="flex items-center gap-2 mb-4">
            <h4 className="text-base font-semibold text-gray-900">Fee Type</h4>
            <Button variant="ghost" size="sm" className="h-auto p-0">
              <Info className="w-4 h-4 text-gray-400" />
            </Button>
          </div>
          <Select
            value={settings.fee_type || 'Per Strategy'}
            onValueChange={(v) => updateSetting('fee_type', v)}
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="Per Strategy">Per Strategy</SelectItem>
              <SelectItem value="Per Trade">Per Trade</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div>
          <div className="flex items-center gap-2 mb-4">
            <h4 className="text-base font-semibold text-gray-900">Monthly Fixed fee</h4>
            <Button variant="ghost" size="sm" className="h-auto p-0">
              <Info className="w-4 h-4 text-gray-400" />
            </Button>
          </div>
          <Input
            type="number"
            value={settings.monthly_fixed_fee || 0}
            onChange={(e) => updateSetting('monthly_fixed_fee', parseInt(e.target.value) || 0)}
            placeholder="0"
          />
        </div>

        <div>
          <div className="flex items-center gap-2 mb-4">
            <h4 className="text-base font-semibold text-gray-900">Variable fee (%)</h4>
            <Button variant="ghost" size="sm" className="h-auto p-0">
              <Info className="w-4 h-4 text-gray-400" />
            </Button>
          </div>
          <Input
            type="number"
            value={settings.variable_fee_percent || 0}
            onChange={(e) => updateSetting('variable_fee_percent', parseInt(e.target.value) || 0)}
            placeholder="0"
          />
        </div>

        <div>
          <h4 className="text-base font-semibold text-gray-900 mb-4">Minimum</h4>
          <Select
            value={settings.minimum_multiple || '1x'}
            onValueChange={(v) => updateSetting('minimum_multiple', v)}
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="1x">1x</SelectItem>
              <SelectItem value="2x">2x</SelectItem>
              <SelectItem value="5x">5x</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div>
          <div className="flex items-center gap-2 mb-4">
            <h4 className="text-base font-semibold text-gray-900">Maximum Multiple for subscriber to deploy</h4>
            <Button variant="ghost" size="sm" className="h-auto p-0">
              <Info className="w-4 h-4 text-gray-400" />
            </Button>
          </div>
          <Select
            value={settings.maximum_multiple || '100x'}
            onValueChange={(v) => updateSetting('maximum_multiple', v)}
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="100x">100x</SelectItem>
              <SelectItem value="50x">50x</SelectItem>
              <SelectItem value="10x">10x</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Display and Invoice Settings */}
      <div className="grid md:grid-cols-2 gap-8">
        <div>
          <div className="flex items-center gap-2 mb-4">
            <h4 className="text-base font-semibold text-gray-900">Display Positions to Non Subscribers</h4>
            <Button variant="ghost" size="sm" className="h-auto p-0">
              <Info className="w-4 h-4 text-gray-400" />
            </Button>
          </div>
          <Select
            value={settings.display_positions_non_subscribers || 'No'}
            onValueChange={(v) => updateSetting('display_positions_non_subscribers', v)}
            className="w-full"
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="No">No</SelectItem>
              <SelectItem value="Yes">Yes</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div>
          <div className="flex items-center gap-2 mb-4">
            <h4 className="text-base font-semibold text-gray-900">Invoice Frequency for Variable Fee</h4>
            <Button variant="ghost" size="sm" className="h-auto p-0">
              <Info className="w-4 h-4 text-gray-400" />
            </Button>
          </div>
          <Select
            value={settings.invoice_frequency || 'Per Month'}
            onValueChange={(v) => updateSetting('invoice_frequency', v)}
            className="w-full"
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="Per Month">Per Month</SelectItem>
              <SelectItem value="Per Week">Per Week</SelectItem>
              <SelectItem value="Per Day">Per Day</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Additional Settings */}
      <div className="grid md:grid-cols-2 gap-8">
        <div>
          <div className="flex items-center gap-2 mb-4">
            <h4 className="text-base font-semibold text-gray-900">Allow others to duplicate strategy</h4>
            <Button variant="ghost" size="sm" className="h-auto p-0">
              <Info className="w-4 h-4 text-gray-400" />
            </Button>
          </div>
          <Select
            value={settings.allow_duplicate || 'No'}
            onValueChange={(v) => updateSetting('allow_duplicate', v)}
            className="w-full"
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="No">No</SelectItem>
              <SelectItem value="Yes">Yes</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div>
          <div className="flex items-center gap-2 mb-4">
            <h4 className="text-base font-semibold text-gray-900">Recreate old positions for new subscriber</h4>
            <Button variant="ghost" size="sm" className="h-auto p-0">
              <Info className="w-4 h-4 text-gray-400" />
            </Button>
          </div>
          <Select
            value={settings.recreate_old_positions || 'No'}
            onValueChange={(v) => updateSetting('recreate_old_positions', v)}
            className="w-full"
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="No">No</SelectItem>
              <SelectItem value="Yes">Yes</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Confirm and Hide Settings */}
      <div className="grid md:grid-cols-2 gap-8">
        <div>
          <div className="flex items-center gap-2 mb-4">
            <h4 className="text-base font-semibold text-gray-900">Confirm each subscriber manually</h4>
            <Button variant="ghost" size="sm" className="h-auto p-0">
              <Info className="w-4 h-4 text-gray-400" />
            </Button>
          </div>
          <Select
            value={settings.confirm_subscriber_manually || 'No'}
            onValueChange={(v) => updateSetting('confirm_subscriber_manually', v)}
            className="w-full"
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="No">No</SelectItem>
              <SelectItem value="Yes">Yes</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div>
          <h4 className="text-base font-semibold text-gray-900 mb-4">Hide Subscribers from others</h4>
          <Select
            value={settings.hide_subscribers || 'No'}
            onValueChange={(v) => updateSetting('hide_subscribers', v)}
            className="w-full"
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="No">No</SelectItem>
              <SelectItem value="Yes">Yes</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
    </div>
  );
};

export default MarketplaceSettingsSection;
